// @flow

import React, { type Node, useEffect } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useUserInputAsTextFieldWithFormatting, useUserInputValue } from '@tectonics/prs/user-input-values';
import { normalizeCustomerDocument } from '@tectonics/prs/customer/normalize';
import { useUserInputValidationError } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';

import { Input, Label } from '../../../../../commons/atoms';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { Field } from '../../../../../commons/molecules';
import { ValidationErrorMessage } from './validation-error-message';

export function FieldColombianDocument(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [colombianDocument, handleChangeColombianDocument] = useUserInputAsTextFieldWithFormatting(
    'colombianDocument',
    formatRaw,
    normalizeCustomerDocument
  );
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('colombianDocument');
  const [, setDocumentType] = useUserInputValue('colombianDocumentType');

  useEffect(() => {
    setDocumentType(getDocumentTypeFromValue(colombianDocument));
  }, [colombianDocument, setDocumentType]);

  return (
    <Field
      data-test-name="customer-document"
      label={<Label htmlFor={inputId}>Documento (CC / CE / NIT)</Label>}
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={formHasBeenSent && !!validationError}
    >
      <Input
        as={InputMask}
        id={inputId}
        value={colombianDocument}
        onChange={handleChangeColombianDocument}
        mask={COLOMBIAN_MASK}
        readOnly={submitting}
        placeholder="Ingresa tu documento"
        type="tel"
        name="customer-document"
      />
    </Field>
  );
}

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, COLOMBIAN_MASK);
}

function getDocumentTypeFromValue(value: string): string {
  if (COLOMBIAN_NIT_REGEX.test(value)) return 'NIT';
  if (value.length >= 2) return 'CC';
  return 'CE';
}

const COLOMBIAN_NIT_REGEX = Object.freeze(/^[8-9]\d{8,9}$/);

const COLOMBIAN_MASK = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
